/**
 * The application shell.
 *
 * The context bar always shows the selected organization, the framework, and
 * the work view. The navigation shows only the sections that the person's
 * roles reach.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

import { Button, Loading, Problem, buttonClass, classes, inputClass } from "./components/primitives.tsx";
import { EffectProvider } from "./components/space/effects/provider.tsx";
import { PasskeyReminder } from "./components/passkey-reminder.tsx";
import { SampleBanner } from "./components/sample-data.tsx";
import { AskCapture } from "./components/ask-capture.tsx";
import { MissionDisplay, MissionLine } from "./components/mission/mission.tsx";
import { useTheme } from "./lib/theme.ts";
import { QuickNoteCapture } from "./components/quick-note.tsx";
import { ReportProblem } from "./components/report-problem.tsx";
import { ThemeChoices } from "./components/theme-menu.tsx";
import { useDismiss } from "./components/use-dismiss.ts";
import { NewOrganizationPage } from "./routes/new-organization.tsx";
import { SignInPage } from "./routes/sign-in.tsx";
import { Icon } from "./components/icon.tsx";
import { api, setCsrfToken, type SessionView } from "./lib/api.ts";
import {
  areaOf,
  areasInGroup,
  groupOf,
  visibleAreas,
  visibleSections as sectionsOf,
  type Visibility,
} from "./lib/navigation.ts";
import {
  MobileBottomNav,
  MobilePageTabs,
  MobileTopBar,
  MoreSheet,
} from "./components/mobile/shell.tsx";

interface WorkspaceChoice {
  readonly frameworkKey: string;
  readonly shortName: string;
  readonly state: string;
}

export function useWorkspaces(tenantId: string | null = null) {
  return useQuery({
    // The list belongs to the selected organization, so a change of
    // organization never shows the previous organization's frameworks.
    queryKey: ["workspaces", tenantId],
    queryFn: () =>
      api.get<{
        workspaces: WorkspaceChoice[];
        selectedWorkspaceKey: string | null;
      }>("/api/workspaces"),
    retry: false,
  });
}

export function useSession() {
  return useQuery({
    queryKey: ["session"],
    queryFn: () => api.get<SessionView>("/api/session"),
    retry: false,
  });
}

/**
 * The permissions the server granted in the selected organization. A page
 * shows or hides a control by them; the server decides every action again.
 */
export function usePermissions(): { readonly has: (permission: string) => boolean; readonly isPlatformAdmin: boolean; readonly ready: boolean } {
  const session = useSession();
  const view = session.data;
  const permissions = view?.organizations.find((row) => row.tenantId === view.selectedTenantId)?.permissions ?? [];
  return { has: (permission) => permissions.includes(permission), isPlatformAdmin: view?.isPlatformAdmin ?? false, ready: view !== undefined };
}

export function AppShell() {
  const session = useSession();
  // The phone's More menu. It is a sheet and not a page, so it holds no
  // address of its own and Back still means the previous page.
  const [more, setMore] = useState(false);
  const queryClient = useQueryClient();
  const routerState = useRouterState();
  const workspaces = useWorkspaces(session.data?.selectedTenantId ?? null);
  // The theme is already painted by the time this runs; the hook keeps it
  // current while the application is open — following the operating system
  // while the choice is System, and correcting this browser from the server's
  // copy on a machine the person has not used before.
  useTheme();

  useEffect(() => {
    if (session.data !== undefined) {
      setCsrfToken(session.data.csrfToken);
    }
  }, [session.data]);

  const selectTenant = useMutation({
    mutationFn: (tenantId: string) => api.post("/api/session/tenant", { tenantId }),
    onSuccess: async () => {
      // A response tagged for the previous organization must never render
      // under the new one. Cancel every in-flight request and drop every
      // cached answer before anything is asked again.
      await queryClient.cancelQueries();
      queryClient.removeQueries();
      await queryClient.invalidateQueries();
    },
  });

  const selectWorkspace = useMutation({
    mutationFn: (workspaceKey: string | null) =>
      api.post("/api/session/workspace", { workspaceKey }),
    onSuccess: async () => {
      await queryClient.invalidateQueries();
    },
  });

  const signOut = useMutation({
    mutationFn: () =>
      api.post<{ signedOut: boolean; endSessionUrl: string | null }>(
        "/api/auth/sign-out",
      ),
    onSuccess: async (result) => {
      await queryClient.clear();
      // The ArcGRC session ended on the server. The identity session ends at
      // the address the server returned, so a second sign-in asks for the
      // credentials again.
      window.location.assign(result.endSessionUrl ?? "/");
    },
  });

  // The employee portal has no member session and no navigation.
  const pathname = routerState.location.pathname;
  if (pathname === "/employee" || pathname.startsWith("/employee/")) {
    return <Outlet />;
  }
  // The end of a passkey replacement. The replacement ended every session,
  // so the page renders without one.
  if (pathname === "/security/recovery/done") {
    return (
      <MinimalFrame>
        <Outlet />
      </MinimalFrame>
    );
  }
  if (session.isLoading) {
    return <Loading what="your session" />;
  }

  // An assessor invitation is accepted before the person has an organization,
  // and sometimes before the person has a session. The accept page renders in
  // both cases: it shows the sign-in prompt itself when the session is missing.
  const acceptingInvitation = pathname === "/audit/accept";
  if (acceptingInvitation && (session.isError || session.data === undefined)) {
    return (
      <MinimalFrame>
        <Outlet />
      </MinimalFrame>
    );
  }

  if (session.isError || session.data === undefined) {
    return <SignInPage />;
  }

  const view = session.data;

  // A person who belongs to no organization has nothing to select and every
  // page would refuse. Their first action is to make one, unless they arrived
  // to accept an assessor invitation, which makes the membership for them.
  if (view.organizations.length === 0 || acceptingInvitation) {
    return (
      <MinimalFrame displayName={view.user.displayName} onSignOut={() => signOut.mutate()}>
        {acceptingInvitation ? <Outlet /> : <NewOrganizationPage first />}
      </MinimalFrame>
    );
  }

  const permissions = view.organizations.find((row) => row.tenantId === view.selectedTenantId)?.permissions ?? [];
  const selected = view.organizations.find(
    (row) => row.tenantId === view.selectedTenantId,
  );
  const active = (workspaces.data?.workspaces ?? []).filter(
    (row) => row.state === "active",
  );
  const workspaceKey = view.selectedWorkspaceKey;


  // What this person sees, in this organization, in this workspace. The three
  // shells — phone, tablet, desktop — all read it, and none of them decides
  // it: the permissions came from the server, which refuses every request
  // again on its own.
  const where: Visibility = {
    permissions,
    activeFrameworks: active.map((row) => row.frameworkKey),
    workspaceKey,
  };
  const currentArea = areaOf(pathname);
  const visibleSections = sectionsOf(currentArea, where);
  // A phone tab can hold more than one area — Records holds the registers and
  // the work that comes round again — so its page tabs are the sections of
  // the whole group. Otherwise a page in the second area of a group would be
  // reachable from nowhere.
  // The area the tab is named after comes first, as the tab opens on it.
  const phoneGroup = groupOf(pathname);
  const phoneSections = [...areasInGroup(phoneGroup, where)]
    .sort((a, b) => (a.key === phoneGroup ? -1 : b.key === phoneGroup ? 1 : 0))
    .map((area) => ({ label: area.label, sections: sectionsOf(area, where) }))
    .filter((area) => area.sections.length > 0);

  return (
    <div className="min-h-screen">
      <a className="skip-link" href="#main">
        Go to the main content
      </a>
      <PasskeyReminder accountUrl={view.accountUrl ?? "#"} />
      <SampleBanner />

      {/*
        The phone's bar: the mark, the organization and workspace as one line,
        and the account. Nothing else stands between a person and the page.
      */}
      <header className="sticky top-0 z-20 border-b border-divider bg-paper tablet:hidden">
        <MobileTopBar
          view={view}
          where={where}
          workspaces={active}
          onSelectTenant={(tenantId) => selectTenant.mutate(tenantId)}
          onSelectWorkspace={(key) => selectWorkspace.mutate(key)}
          onSignOut={() => signOut.mutate()}
        />
        <MissionDisplay tenantId={view.selectedTenantId ?? null} phone />
      </header>

      {/*
        The wide screens. A tablet is the desktop's information architecture
        at a lower density: the same rows, the same controls, less padding,
        and the words that only label a control they already name are left to
        the desktop. It is not a shrunk desktop and it is not a wide phone.
      */}
      <header className="hidden border-b border-divider bg-paper tablet:block">
        <div className="flex flex-wrap items-center gap-2 px-4 py-2 desktop:gap-4 desktop:px-6 desktop:py-3">
          <Link to="/" className="flex items-center gap-3">
            <img src="/brand/arcgrc-logo.svg" alt="ArcGRC" className="h-7 w-auto" />
          </Link>
          <div className="ml-auto flex flex-wrap items-center gap-3">
            {/* The control names the organization; the word above it does
                not, so a tablet spends the width on the name instead. */}
            <label htmlFor="organization" className="hidden text-sm font-medium desktop:inline">
              Organization
            </label>
            <div className="flex items-center">
              <select
                id="organization"
                className={classes(inputClass, "w-auto rounded-r-none py-1.5")}
                value={view.selectedTenantId ?? ""}
                onChange={(event) => selectTenant.mutate(event.target.value)}
              >
                {view.organizations.map((row) => (
                  <option key={row.tenantId} value={row.tenantId}>
                    {row.name}
                  </option>
                ))}
              </select>
              {/*
                Every signed-in person may make an organization, so this is not
                hidden behind an answer from the server. A hidden control that
                should be there is worse than one that answers with a reason.
              */}
              <Link
                to="/organizations/new"
                title="Make a new organization"
                aria-label="Make a new organization"
                className={classes(buttonClass("secondary", "small"), "-ml-px self-stretch rounded-l-none text-accent")}
              >
                <Icon name="plus" size="0.8rem" />
                New
              </Link>
            </div>
            {active.length > 0 && (
              <>
                <label htmlFor="workspace" className="hidden text-sm font-medium desktop:inline">
                  Workspace
                </label>
                <select
                  id="workspace"
                  className={classes(inputClass, "w-auto py-1.5")}
                  // Off while the list of the newly selected organization loads.
                  disabled={workspaces.isFetching || selectWorkspace.isPending}
                  value={workspaceKey ?? ""}
                  onChange={(event) =>
                    selectWorkspace.mutate(
                      event.target.value === "" ? null : event.target.value,
                    )
                  }
                >
                  <option value="">Every framework</option>
                  {active.map((row) => (
                    <option key={row.frameworkKey} value={row.frameworkKey}>
                      {row.shortName}
                    </option>
                  ))}
                </select>
              </>
            )}
            <MissionDisplay tenantId={view.selectedTenantId ?? null} />
            <div className="relative">
              <AskCapture />
            </div>
            <div className="relative">
              <QuickNoteCapture />
            </div>
            <ReportProblem />
            <AccountMenu view={view} onSignOut={() => signOut.mutate()} />
          </div>
        </div>
        <nav aria-label="Areas" className="flex flex-wrap gap-0.5 px-2 desktop:gap-1 desktop:px-4">
          {visibleAreas(where).map((area) => {
            const current = area.key === currentArea.key;
            return (
              <Link
                key={area.key}
                to={sectionsOf(area, where)[0]!.to}
                aria-current={current ? "page" : undefined}
                className={classes(
                  "inline-flex items-center gap-1.5 rounded-t-arc px-2 py-2 text-sm desktop:px-3",
                  current
                    ? "border-b-2 border-accent font-semibold text-accent"
                    : "text-muted hover:text-ink",
                )}
              >
                <Icon name={area.icon} size="0.9rem" />
                {area.label}
              </Link>
            );
          })}
        </nav>
        {/*
          The blue band belongs to the sections row alone. It used to sit on
          an organization banner as well, which the organization selector
          already says; keeping the colour to one row makes the second level
          of navigation read as a second level.
        */}
        {visibleSections.length > 1 && (
          <nav
            aria-label="Sections"
            className="flex flex-wrap gap-1 border-t border-divider bg-selected px-2 py-1 desktop:px-4"
          >
            {visibleSections.map((section) => {
              const current =
                section.to === "/"
                  ? routerState.location.pathname === "/"
                  : routerState.location.pathname.startsWith(section.to);
              return (
                <Link
                  key={section.to}
                  to={section.to}
                  aria-current={current ? "page" : undefined}
                  className={classes(
                    "inline-flex items-center gap-1.5 rounded-arc px-3 py-1 text-sm",
                    current ? "bg-paper font-semibold text-accent" : "text-ink hover:bg-paper",
                  )}
                >
                  <Icon name={section.icon} size="0.85rem" className={current ? "" : "text-muted"} />
                  {section.label}
                </Link>
              );
            })}
          </nav>
        )}
      </header>

      {/*
        The bottom edge of the header, while an assessment window is open. It
        is the light theme's version of the star field, and the dark theme
        carries both. It is drawn once, under whichever header is on the
        screen, because both are in the document and only one is displayed.
      */}
      <MissionLine tenantId={view.selectedTenantId ?? null} />

      {selected === undefined && (
        <div className="px-4 py-4 tablet:px-6">
          <Problem message="Select an organization to continue." />
        </div>
      )}

      {/*
        The space effects live here and nowhere else. The minimal frames
        above — sign-in, the invitation page, the employee portal — render
        outside this provider, so no scene can start on them at all.

        The bottom padding on a phone clears the navigation bar and the home
        indicator under it, so the last row of a list is never covered.
      */}
      <main id="main" className="arc-main px-4 py-4 tablet:py-5 desktop:px-6 desktop:py-6">
        {/*
          The sections of this area, as tabs inside the page. On a phone
          secondary navigation belongs to the page and not to the
          application's bar; the wide screens keep the row in the header.
        */}
        <MobilePageTabs areas={phoneSections} pathname={pathname} />
        <EffectProvider
          userId={view.user.id}
          tenantId={view.selectedTenantId}
          workspaceKey={view.selectedWorkspaceKey}
        >
          <Outlet />
        </EffectProvider>
      </main>

      <MobileBottomNav
        where={where}
        pathname={pathname}
        moreOpen={more}
        onMore={() => setMore(true)}
      />
      <MoreSheet open={more} onClose={() => setMore(false)} where={where} />
    </div>
  );
}

/** A header with the logo only, for a page that runs outside an organization. */
function MinimalFrame({
  displayName,
  onSignOut,
  children,
}: {
  readonly displayName?: string;
  readonly onSignOut?: () => void;
  readonly children: ReactNode;
}) {
  return (
    <div className="min-h-screen bg-canvas text-ink">
      <header className="border-b border-divider bg-paper">
        <div className="flex items-center gap-4 px-6 py-3">
          <img src="/brand/arcgrc-logo.svg" alt="ArcGRC" className="h-7 w-auto" />
          {displayName !== undefined && (
            <span className="ml-auto text-sm text-muted">{displayName}</span>
          )}
          {onSignOut !== undefined && (
            <Button type="button" variant="secondary" onClick={onSignOut}>
              Sign out
            </Button>
          )}
        </div>
      </header>
      <main id="main" className="mx-auto max-w-2xl px-6 py-10">
        {children}
      </main>
    </div>
  );
}

/** The person's name, and the small menu beneath it. */
function AccountMenu({
  view,
  onSignOut,
}: {
  readonly view: SessionView;
  readonly onSignOut: () => void;
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(box, open, close);
  return (
    <div ref={box} className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
        className="rounded-arc px-2 py-1 text-sm hover:bg-selected"
        onClick={() => setOpen(!open)}
      >
        {view.user.displayName}
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-10 mt-1 flex w-56 flex-col gap-1 rounded-arc border border-divider bg-paper p-2 text-sm shadow"
        >
          <span className="px-2 py-1 text-muted">{view.user.email}</span>
          <Link
            to="/account"
            role="menuitem"
            className="flex items-center gap-2 rounded-arc px-2 py-1 hover:bg-selected"
            onClick={close}
          >
            <Icon name="settings" size="0.85rem" className="text-muted" />
            Your settings
          </Link>
          <Link
            to="/security/me"
            role="menuitem"
            className="flex items-center gap-2 rounded-arc px-2 py-1 hover:bg-selected"
            onClick={close}
          >
            <Icon name="shield" size="0.85rem" className="text-muted" />
            My security
          </Link>
          {view.accountUrl !== null && (
            <a
              role="menuitem"
              className="flex items-center gap-2 rounded-arc px-2 py-1 hover:bg-selected"
              href={view.accountUrl}
              target="_blank"
              rel="noreferrer"
              onClick={close}
            >
              <Icon name="key" size="0.85rem" className="text-muted" />
              Manage passkeys
            </a>
          )}
          {/* The same three choices a phone keeps in its account sheet. */}
          <ThemeChoices onChosen={close} />
          <button
            type="button"
            role="menuitem"
            className="min-h-11 rounded-arc px-2 py-1 text-left hover:bg-selected"
            onClick={onSignOut}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
